import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store/store';

import { DocumentType } from 'app/types/document-type';
import { ViolationDetails } from 'app/types/driving-violations';
import { AsyncSelectOptions } from 'app/types/select-options';

import { AccountProperty } from 'app/services/api/models/AccountProperty';
import { DriverStatusStats } from 'app/services/api/models/DriverStatusStats';

import {
  DEFAULT_STATUS_FILTER_VALUE,
  driverStatusState,
} from './driverStatusState';
import { ColumnMeta, DecoratedDriverStatus } from '../types';

export const driverStatusSlice = createSlice({
  name: 'driverStatus',
  initialState: driverStatusState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // ---------------------------------------------------------
    // PAGINATION
    // ---------------------------------------------------------
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    setTotalRecordCounts: (state, action: PayloadAction<number>) => {
      state.totalRecordCount = action.payload;
    },

    // ---------------------------------------------------------
    // DATA SOURCE
    // ---------------------------------------------------------
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortField: (state, action: PayloadAction<string>) => {
      state.sortField = action.payload;
    },
    setDataReset: state => {
      state.driver = null;
      state.terminal = null;
      state.subset = null;
      state.status = DEFAULT_STATUS_FILTER_VALUE;
      state.dutyStatusEventType = null;
      state.driverStatuses = [];
      state.driverStatusStats = {
        OffDuty: 0,
        Sleeper: 0,
        Driving: 0,
        OnDuty: 0,
        Waiting: 0,
      };
    },
    setDriverViolationDetails: (
      state,
      action: PayloadAction<ViolationDetails | null>,
    ) => {
      state.driverViolationDetails = action.payload;
    },
    setDriverStatuses: (
      state,
      action: PayloadAction<Array<DecoratedDriverStatus>>,
    ) => {
      state.driverStatuses = action.payload;
    },
    setDriverStatusStats: (state, action: PayloadAction<DriverStatusStats>) => {
      const {
        OffDuty,
        OnDuty,
        Sleeper,
        Driving,
        WaitingAtSite,
      } = action.payload['Event Type Totals'];

      state.driverStatusStats = {
        OffDuty: OffDuty ?? 0,
        Sleeper: Sleeper ?? 0,
        Driving: Driving ?? 0,
        OnDuty: OnDuty ?? 0,
        Waiting: WaitingAtSite ?? 0,
      };
    },

    // ---------------------------------------------------------
    // EXPORT FILE DATA SOURCE
    // ---------------------------------------------------------
    setDocumentType: (state, action: PayloadAction<DocumentType>) => {
      state.documentType = action.payload;
    },
    setDriverStatusesExport: (
      state,
      action: PayloadAction<Array<DecoratedDriverStatus>>,
    ) => {
      state.driverStatusesExport = action.payload;
    },

    // ---------------------------------------------------------
    // API REQUEST STATUS
    // ---------------------------------------------------------
    setRequestPending: (state, action: PayloadAction<boolean>) => {
      state.isRequestPending = action.payload;
    },
    setAccountPropsRequestPending: (state, action: PayloadAction<boolean>) => {
      state.isAccountPropsRequestPending = action.payload;
    },
    setDriverStatusesExportRequestPending: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isDriverStatusesExportRequestPending = action.payload;
    },

    // ---------------------------------------------------------
    // SHOW/HIDE COLUMNS MODAL
    // ---------------------------------------------------------
    setSortedColumn: (state, action: PayloadAction<Array<ColumnMeta>>) => {
      state.sortedColumns = action.payload;
    },
    setVisibleColumns: (state, action: PayloadAction<Array<ColumnMeta>>) => {
      state.columns = action.payload;
    },
    setPortalColumnSettings: (
      state,
      action: PayloadAction<Array<AccountProperty>>,
    ) => {
      state.portalColumnSettings = action.payload;
    },

    // ---------------------------------------------------------
    // MODAL TOGGLERS
    // ---------------------------------------------------------
    setToggleExportFileModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleExportFileModal = action.payload;
    },
    setToggleShowHideColumnsModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleShowHideColumnsModal = action.payload;
    },
    setToggleDrivingViolationsModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleDrivingViolationsModal = action.payload;
    },

    // ---------------------------------------------------------
    // FILTERS
    // ---------------------------------------------------------
    setSelectedDriver: (
      state,
      action: PayloadAction<AsyncSelectOptions | null>,
    ) => {
      state.driver = action.payload;
    },
    setSelectedTerminal: (
      state,
      action: PayloadAction<AsyncSelectOptions | null>,
    ) => {
      state.terminal = action.payload;
    },
    setSelectedSubset: (
      state,
      action: PayloadAction<AsyncSelectOptions | null>,
    ) => {
      state.subset = action.payload;
    },
    setSelectedStatus: (state, action: PayloadAction<string | number>) => {
      state.status = action.payload;
    },
    setDutyStatusEventType: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.dutyStatusEventType = action.payload;
    },
    setResetData: () => driverStatusState,
  },
});

/* ------- ACTION CREATORS ------- */
export const { actions: driverStatusActions } = driverStatusSlice;

/* ------- STATE SELECTORS ------- */
export const selectDriverStatus = (state: RootState) =>
  state.driverStatus || driverStatusState;

// Pagination
export const makeSelectLimit = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.limit);

export const makeSelectPageIndex = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.pageIndex);

export const makeSelectTotalRecordCount = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.totalRecordCount,
  );

// Data Source
export const makeSelectDataSortBy = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.sortBy);

export const makeSelectSortField = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.sortField);

export const makeSelectDriverViolationDetails = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.driverViolationDetails,
  );

export const makeSelectDriverStatuses = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.driverStatuses,
  );

export const makeSelectDriverStatusStats = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.driverStatusStats,
  );

// Export file data source
export const makeSelectDocumentType = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.documentType);

export const makeSelectDriverStatusesExport = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.driverStatusesExport,
  );

// API Request
export const makeSelectRequestPending = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.isRequestPending,
  );

export const makeSelectAccountPropsRequestPending = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.isAccountPropsRequestPending,
  );

export const makeSelectDriverStatusesExportRequestPending = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.isDriverStatusesExportRequestPending,
  );

// Show/Hide Columns Modal
export const makeSelectColumns = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.columns);

export const makeSelectSortedColumns = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.sortedColumns,
  );

export const makeSelectPortalColumnsSettings = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.portalColumnSettings,
  );

// Modal Togglers
export const makeSelectToggleExportFileModal = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.isToggleExportFileModal,
  );

export const makeSelectToggleShowHideColumnsModal = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.isToggleShowHideColumnsModal,
  );

export const makeSelectToggleDrivingViolationsModal = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.isToggleDrivingViolationsModal,
  );

// Filters
export const makeSelectDriver = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.driver);

export const makeSelectTerminal = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.terminal);

export const makeSelectSubset = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.subset);

export const makeSelectStatus = () =>
  createSelector(selectDriverStatus, driverStatus => driverStatus.status);

export const makeSelectDutyStatusEventType = () =>
  createSelector(
    selectDriverStatus,
    driverStatus => driverStatus.dutyStatusEventType,
  );

export default driverStatusSlice.reducer;
